import { Body1, Caption1, Dropdown, Option, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

import { SECTION_STATUS, TChangeV2, TSectionStatus } from '../../models';
import { getVisibleSections } from '../../utils';
import { SectionStatusBadge } from './SectionStatusBadge';

const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: tokens.spacingVerticalXXS,
    width: '100%',
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalXXS,
    width: '100%',
    boxSizing: 'border-box',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    border: '1px solid transparent',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground1,
    fontFamily: tokens.fontFamilyBase,
    textAlign: 'left',
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  active: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    boxShadow: tokens.shadow2,
  },
  title: { display: 'block', width: '100%', textAlign: 'left' },
  caption: { display: 'block', width: '100%', textAlign: 'left' },
  status: { marginTop: tokens.spacingVerticalXXS, whiteSpace: 'nowrap' },
  muted: { color: tokens.colorNeutralForeground3 },
});

export type TNavItem = {
  id: string;
  title: string;
  caption?: string;
  status?: TSectionStatus;
};

// The ordered navigator model: the visible configured sections, followed by the
// affected objects and the assessment summary.
export const getAssessmentNavItems = (change: TChangeV2): TNavItem[] => {
  const assessment = change.assessment;
  if (!assessment) return [];
  const sections = getVisibleSections(assessment, change).map<TNavItem>((section) => ({
    id: section.id,
    title: section.title,
    caption: section.responsibleFunction,
    status: assessment.sectionProgress[section.id]?.status ?? SECTION_STATUS.NOT_STARTED,
  }));
  return [
    ...sections,
    { id: 'affected-objects', title: 'Affected documents' },
    { id: 'summary', title: 'Assessment summary' },
  ];
};

type TProps = {
  change: TChangeV2;
  activeId: string;
  onSelect: (id: string) => void;
  variant?: 'list' | 'dropdown';
};

// The compact left navigator (or a dropdown on narrow screens). Each item shows a
// short title, its responsible function and a status indicator - no long text.
export const SectionNavigator = ({ change, activeId, onSelect, variant = 'list' }: TProps) => {
  const classes = useStyles();
  const items = getAssessmentNavItems(change);
  const activeItem = items.find((item) => item.id === activeId);

  if (variant === 'dropdown') {
    return (
      <Dropdown
        selectedOptions={[activeId]}
        value={activeItem?.title ?? ''}
        onOptionSelect={(_, data) => data.optionValue && onSelect(data.optionValue)}
      >
        {items.map((item) => (
          <Option key={item.id} value={item.id} text={item.title}>
            {item.title}
          </Option>
        ))}
      </Dropdown>
    );
  }

  return (
    <div className={classes.list} role="tablist" aria-label="Assessment sections">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={item.id === activeId}
          className={mergeClasses(classes.item, item.id === activeId && classes.active)}
          onClick={() => onSelect(item.id)}
        >
          <Body1 className={classes.title}>{item.title}</Body1>
          {item.caption ? (
            <Caption1 className={mergeClasses(classes.caption, classes.muted)}>{item.caption}</Caption1>
          ) : null}
          {item.status ? <SectionStatusBadge className={classes.status} status={item.status} /> : null}
        </button>
      ))}
    </div>
  );
};
